import { startOfMonth, endOfMonth, eachDayOfInterval, isMonday, isThursday, isSaturday, isSunday, addWeeks, setMonth, setDate, format, addDays } from 'date-fns';

// Helper to calculate Easter Date (Anonymous/Meeus/Jones/Butcher algorithm)
const getEasterDate = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    // Return formatted as YYYY-MM-DD
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// Helper to find specific occurrence of a weekday in a month
// occurrence: 1 (1st), 2 (2nd), 3 (3rd), 4 (4th), 'last'
const getFloatingHoliday = (year, month, dayOfWeekName, occurrence) => {
    const startDate = new Date(year, month, 1); // month is 0-indexed (0=Jan)
    const endDate = endOfMonth(startDate);

    const days = eachDayOfInterval({ start: startDate, end: endDate });

    let matches = [];

    switch(dayOfWeekName) {
        case 'Monday': matches = days.filter(isMonday); break;
        case 'Thursday': matches = days.filter(isThursday); break;
        case 'Saturday': matches = days.filter(isSaturday); break;
        case 'Sunday': matches = days.filter(isSunday); break;
        default: return null;
    }

    if (occurrence === 'last') {
        return format(matches[matches.length - 1], 'yyyy-MM-dd');
    }

    if (matches[occurrence - 1]) {
        return format(matches[occurrence - 1], 'yyyy-MM-dd');
    }

    return null;
};

// ⚡ Bolt Optimization: Cache expensive holiday calculations per year
// Why: getHolidays computes floating holidays (like Thanksgiving) by iterating
// over every day of a month. It's called multiple times per calendar render.
// Impact: Reduces O(days in month) calculations to O(1) for subsequent lookups
// of the same year, improving Marketing Calendar render performance.
const holidayCache = new Map();

export const getHolidays = (year) => {
    const y = parseInt(year);

    if (holidayCache.has(y)) {
        return holidayCache.get(y);
    }

    // Dynamic Calculations
    const mlkDay = getFloatingHoliday(y, 0, 'Monday', 3); // 3rd Mon in Jan
    const presidentsDay = getFloatingHoliday(y, 1, 'Monday', 3); // 3rd Mon in Feb
    const memorialDay = getFloatingHoliday(y, 4, 'Monday', 'last'); // Last Mon in May
    const laborDay = getFloatingHoliday(y, 8, 'Monday', 1); // 1st Mon in Sep
    const thanksgiving = getFloatingHoliday(y, 10, 'Thursday', 4); // 4th Thu in Nov

    // Derived from Thanksgiving
    const thanksgivingDate = new Date(thanksgiving);

    // Small Business Saturday (Saturday after Thanksgiving) = +2 days
    const smallBizSatDate = addDays(thanksgivingDate, 2);
    const smallBizSat = format(smallBizSatDate, 'yyyy-MM-dd');

    // Giving Tuesday (Tuesday after Thanksgiving) = +5 days
    const givingTuesdayDate = addDays(thanksgivingDate, 5);
    const givingTuesday = format(givingTuesdayDate, 'yyyy-MM-dd');

    const easter = getEasterDate(y);

    const holidays = [
      // Fixed Date Holidays
      { date: `${y}-01-01`, title: "New Year's Day", type: 'holiday' },
      { date: `${y}-02-14`, title: "Valentine's Day", type: 'holiday' },
      { date: `${y}-03-17`, title: "St. Patrick's Day", type: 'holiday' },
      { date: `${y}-06-19`, title: "Juneteenth", type: 'holiday' },
      { date: `${y}-07-04`, title: "Independence Day", type: 'holiday' },
      { date: `${y}-10-31`, title: "Halloween", type: 'holiday' },
      { date: `${y}-11-11`, title: "Veterans Day", type: 'holiday' },
      { date: `${y}-12-25`, title: "Christmas Day", type: 'holiday' },
      { date: `${y}-12-31`, title: "New Year's Eve", type: 'holiday' },

      // Variable Date Holidays
      { date: mlkDay, title: "Martin Luther King Jr. Day", type: 'holiday' },
      { date: presidentsDay, title: "Presidents' Day", type: 'holiday' },
      { date: easter, title: "Easter Sunday", type: 'holiday' },
      { date: memorialDay, title: "Memorial Day", type: 'holiday' },
      { date: laborDay, title: "Labor Day", type: 'holiday' },
      { date: thanksgiving, title: "Thanksgiving Day", type: 'holiday' },

      // Seasonal (Approximate/Fixed for MVP)
      { date: `${y}-03-20`, title: "First Day of Spring", type: 'seasonal' },
      { date: `${y}-06-20`, title: "First Day of Summer", type: 'seasonal' },
      { date: `${y}-09-22`, title: "First Day of Fall", type: 'seasonal' },
      { date: `${y}-12-21`, title: "First Day of Winter", type: 'seasonal' },

      // Dedicated Observance Days
      { date: `${y}-01-24`, title: "Compliment Day", type: 'observance' },
      { date: `${y}-02-04`, title: "World Cancer Day", type: 'observance' },
      { date: `${y}-03-08`, title: "Intl. Women's Day", type: 'observance' },
      { date: `${y}-04-22`, title: "Earth Day", type: 'observance' },
      { date: `${y}-05-04`, title: "Star Wars Day", type: 'observance' },
      { date: `${y}-06-08`, title: "World Oceans Day", type: 'observance' },
      { date: `${y}-07-17`, title: "World Emoji Day", type: 'observance' },
      { date: `${y}-08-19`, title: "World Photography Day", type: 'observance' },
      { date: `${y}-09-21`, title: "Intl. Day of Peace", type: 'observance' },
      { date: `${y}-10-10`, title: "World Mental Health Day", type: 'observance' },
      { date: smallBizSat, title: "Small Business Saturday", type: 'observance' },
      { date: givingTuesday, title: "Giving Tuesday", type: 'observance' },
    ].sort((a, b) => a.date.localeCompare(b.date));

    holidayCache.set(y, holidays);
    return holidays;
};
